import { useEffect, useState } from "react"
import { useFormikContext } from "formik"
import { makeStyles } from "tss-react/mui"
import { Stack } from "@mui/material"

import { MeetingServiceConfig, DynamicConfigKey, MeetingServiceTabValues } from "../form/formTypes"
import { MeetingServiceTimeErrorDialog } from "./orderSummary/MeetingServiceTimeErrorDialog"
import { DimensionsGuardManager } from "./fieldGuards/DimensionsGuardManager"
import { ConfirmOrderSubmitButton } from "../ConfirmOrderSubmitButton"
import { BillReceiverGuard } from "./fieldGuards/BillReceiverGuard"
import { CreateOrderDialogMode, CreateOrderDialogType, SelectedMainUserState } from "../../createOrderTypes"
import { useCreateOrderDialogType } from "../../hooks/useCreateOrderDialogType"
import { INVALID_DELIVERIES } from "../form/helpers"
import { DrawerSections } from "./DrawerSections"
import { AutoAddedServicesNotifications } from "./messages/AutoAddedServicesNotifications"
import { Messages } from "./messages/Messages"

export const DRAWER_WIDTH = "27.5rem"

const defaultDialogState = {
    show: false,
    times: [],
}

const useStyles = makeStyles()((theme) => ({
    content: {
        borderLeft: `1px solid ${theme.palette.tailwind.slate[300]}`,
        background: theme.palette.common.white,
        padding: "1rem",
        justifyContent: "space-between",
        height: "100%",
        overflow: "hidden",
    },
    container: {
        overflow: "auto",
        flexGrow: 1,
    },
}))

type DrawerContentProps = Readonly<{
    meetingServiceConfiguration: MeetingServiceConfig
    dialogType: CreateOrderDialogType
    user: SelectedMainUserState
    mode: CreateOrderDialogMode
    merchantId?: number
}>

export function DrawerContent({ meetingServiceConfiguration, dialogType, user, mode, merchantId }: DrawerContentProps) {
    const { classes } = useStyles()
    const { sections } = meetingServiceConfiguration
    const { errors, setFieldValue, isSubmitting } = useFormikContext<MeetingServiceTabValues>()
    const { isRefundOrder } = useCreateOrderDialogType(dialogType)

    const [expandedAccordionKey, setExpandedAccordionKey] = useState<string | null>("section-0")
    const [showInvalidTimesModal, setShowInvalidTimesModal] = useState<{ show: boolean; times: string[] }>(
        defaultDialogState,
    )

    useEffect(() => {
        if (isSubmitting && Object.keys(errors).length > 0) {
            const errorKeys = Object.keys(errors)

            if (errorKeys.includes(DynamicConfigKey.MULTIPLE_DELIVERIES)) {
                const invalidTimes = errors[DynamicConfigKey.MULTIPLE_DELIVERIES]
                if (typeof invalidTimes === "string" && invalidTimes.startsWith(INVALID_DELIVERIES)) {
                    const times = invalidTimes.replace(INVALID_DELIVERIES, "").split(",") || []
                    if (times.length > 0) {
                        setShowInvalidTimesModal({
                            show: true,
                            times,
                        })
                    }
                }
                return
            }

            for (let i = 0; i < sections.length; i++) {
                if (sections[i].groups.some((g) => g.fields.some((f) => errorKeys.includes(f.key)))) {
                    setExpandedAccordionKey(`section-${i}`)
                    break
                }
            }
        }
    }, [isSubmitting, errors, sections])

    const handleDialogClose = () => {
        if (showInvalidTimesModal.times.length > 0) {
            const firstInvalidTime = showInvalidTimesModal.times[0]
            setFieldValue(`${DynamicConfigKey.MULTIPLE_DELIVERIES}.activeTime`, firstInvalidTime)
        }
        setShowInvalidTimesModal(defaultDialogState)
    }

    return (
        <Stack gap="1.5rem" className={classes.content}>
            <Stack gap=".75rem" className={classes.container}>
                <AutoAddedServicesNotifications dialogType={dialogType} />
                <Messages />
                <DrawerSections
                    meetingServiceConfiguration={meetingServiceConfiguration}
                    expandedAccordionKey={expandedAccordionKey}
                    setExpandedAccordionKey={setExpandedAccordionKey}
                    dialogType={dialogType}
                    user={user}
                    merchantId={merchantId}
                />
            </Stack>
            <ConfirmOrderSubmitButton isRefundOrder={isRefundOrder} />

            {showInvalidTimesModal.show && (
                <MeetingServiceTimeErrorDialog
                    show={showInvalidTimesModal.show}
                    times={showInvalidTimesModal.times}
                    handleDialogClose={handleDialogClose}
                />
            )}

            {mode !== "create" && (
                <>
                    <DimensionsGuardManager userId={user.userId} />
                    <BillReceiverGuard userId={user.userId} />
                </>
            )}
        </Stack>
    )
}
